import React from 'react';
import styled from 'styled-components';

import type { ISimpleResourceType } from '@/components/common';

import { SkeletonBlock } from '@/components/common/loaders/SkeletonBlock';

interface IResourceModalSkeletonProps {
  resourceType: ISimpleResourceType;
}

const ResourceModalSkeleton = ({ resourceType }: IResourceModalSkeletonProps): React.ReactElement => {
  switch (resourceType) {
    case 'announcement':
      return <AnnouncementModalSkeleton />;
    case 'document':
      return <DocumentModalSkeleton />;
    case 'link':
      return <LinkModalSkeleton />;
  }
};

export { ResourceModalSkeleton };

const AnnouncementModalSkeleton = (): React.ReactElement => (
  <SkeletonContainer>
    <SkeletonHorizontalContainer>
      <SimpleInputSkeleton />
      <SimpleInputSkeleton />
    </SkeletonHorizontalContainer>
    <SimpleInputSkeleton />
    <SkeletonSimpleInputContainer>
      <SkeletonInputTitle />
      <SkeletonMultiInputField height={206} />
    </SkeletonSimpleInputContainer>
    <SkeletonSimpleInputContainer>
      <SkeletonInputTitle />
      <SkeletonMultiInputField height={104} />
    </SkeletonSimpleInputContainer>
    <SkeletonHorizontalContainer>
      <DateInputSkeleton />
      <DateInputSkeleton />
    </SkeletonHorizontalContainer>
    <SkeletonInputTitle />
    <SkeletonInputTitle />
    <SimpleInputSkeleton />
  </SkeletonContainer>
);

const DocumentModalSkeleton = (): React.ReactElement => (
  <SkeletonContainer>
    <SimpleInputSkeleton />
    <SimpleInputSkeleton />
    <SkeletonSimpleInputContainer>
      <SkeletonInputTitle />
      <SkeletonMultiInputField height={120} />
    </SkeletonSimpleInputContainer>
    <SimpleInputSkeleton />
    <SkeletonHorizontalContainer>
      <DateInputSkeleton />
      <DateInputSkeleton />
    </SkeletonHorizontalContainer>
    <SimpleInputSkeleton />
  </SkeletonContainer>
);

const LinkModalSkeleton = (): React.ReactElement => (
  <SkeletonContainer>
    <SimpleInputSkeleton />
    <SimpleInputSkeleton />
    <SimpleInputSkeleton />
    <SkeletonSimpleInputContainer>
      <SkeletonInputTitle />
      <SkeletonMultiInputField height={120} />
    </SkeletonSimpleInputContainer>
    <SkeletonHorizontalContainer>
      <DateInputSkeleton />
      <DateInputSkeleton />
    </SkeletonHorizontalContainer>
    <SkeletonInputTitle />
    <SimpleInputSkeleton />
  </SkeletonContainer>
);

const SimpleInputSkeleton = (): React.ReactElement => (
  <SkeletonSimpleInputContainer>
    <SkeletonInputTitle />
    <SkeletonInputField />
  </SkeletonSimpleInputContainer>
);

const DateInputSkeleton = (): React.ReactElement => (
  <SkeletonSimpleInputContainer>
    <SkeletonInputTitle />
    <SkeletonHorizontalContainer>
      <SkeletonInputField />
      <SkeletonInputField />
    </SkeletonHorizontalContainer>
  </SkeletonSimpleInputContainer>
);

const SkeletonContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  row-gap: 24px;
  padding: 2rem 1rem;
`;

const SkeletonSimpleInputContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 8px 0;
`;

const SkeletonHorizontalContainer = styled.div`
  width: 100%;
  display: flex;
  padding: 8px 0;
  column-gap: 24px;
`;

const SkeletonInputTitle = styled(SkeletonBlock)`
  height: 24px;
  margin-bottom: 4px;
`;

const SkeletonInputField = styled(SkeletonBlock)`
  height: 46px;
  margin-top: 3px;
`;

const SkeletonMultiInputField = styled(SkeletonBlock)<{ height: number }>`
  height: ${props => `${props.height}px`};
  margin: 3px 0;
`;
